import { useId, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MdAccessTime } from 'react-icons/md';
import Floated from '@components/Floated';
import styles_input from '@styles/InputContainerLarge.module.scss';
import styles from './TimePickerField.module.scss';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
// Minutes in fixed steps — transfer pickups don't need single-minute precision.
const MINUTE_STEP = 10;
const MINUTES = Array.from({ length: 60 / MINUTE_STEP }, (_, i) => String(i * MINUTE_STEP).padStart(2, '0'));

// Accessible single-column listbox (roving aria-activedescendant + arrow/Enter keys).
function TimeColumn({ label, options, value, onPick }) {
    const baseId = useId();
    const listRef = useRef(null);
    const activeRef = useRef(null);
    const centered = useRef(false);
    const [activeIndex, setActiveIndex] = useState(() => Math.max(0, options.indexOf(value)));

    useLayoutEffect(() => {
        const list = listRef.current;
        const item = activeRef.current;
        if (!list || !item) return;
        if (!centered.current) {
            // Center the selected/active option on open. .column is position:relative,
            // so item.offsetTop is measured from the column itself.
            list.scrollTop = item.offsetTop - (list.clientHeight - item.clientHeight) / 2;
            centered.current = true;
        } else {
            item.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    const onKeyDown = (e) => {
        let next = activeIndex;
        switch (e.key) {
            case 'ArrowDown':
                next = Math.min(options.length - 1, activeIndex + 1);
                break;
            case 'ArrowUp':
                next = Math.max(0, activeIndex - 1);
                break;
            case 'Home':
                next = 0;
                break;
            case 'End':
                next = options.length - 1;
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                onPick(options[activeIndex]);
                return;
            default:
                return;
        }
        e.preventDefault();
        setActiveIndex(next);
    };

    // One delegated click handler instead of 24/60 inline closures.
    const onClick = (e) => {
        const opt = e.target.getAttribute('data-value');
        if (opt == null) return;
        onPick(opt);
        setActiveIndex(options.indexOf(opt));
    };

    return (
        <div
            ref={listRef}
            className={styles.column}
            role="listbox"
            aria-label={label}
            tabIndex={0}
            aria-activedescendant={`${baseId}-${options[activeIndex]}`}
            onKeyDown={onKeyDown}
            onClick={onClick}
        >
            {options.map((opt, i) => (
                <div
                    key={opt}
                    id={`${baseId}-${opt}`}
                    ref={i === activeIndex ? activeRef : null}
                    role="option"
                    aria-selected={opt === value}
                    data-value={opt}
                    className={`${styles.option}${opt === value ? ` ${styles.option_selected}` : ''}${
                        i === activeIndex ? ` ${styles.option_active}` : ''
                    }`}
                >
                    {opt}
                </div>
            ))}
        </div>
    );
}

TimeColumn.propTypes = {
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.string).isRequired,
    value: PropTypes.string,
    onPick: PropTypes.func.isRequired,
};

// Popup body. Holds a draft so a half-picked time is never committed (matches the
// native input's all-or-nothing behaviour); commits only via 确定, then closes.
function TimePickerPopup({ value, onCommit, onClear, onClose }) {
    const rootRef = useRef(null);
    const [h, setH] = useState(() => (value || '').split(':')[0] || '');
    const [m, setM] = useState(() => (value || '').split(':')[1] || '');
    const complete = h !== '' && m !== '';

    // Move focus into the popup on open so keyboard users land inside and Esc works.
    useLayoutEffect(() => {
        rootRef.current?.querySelector('[role="listbox"]')?.focus();
    }, []);

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
        }
    };

    return (
        <div ref={rootRef} className={styles.time_picker} role="dialog" aria-label="选择时间" onKeyDown={onKeyDown}>
            <div className={styles.columns}>
                <TimeColumn label="小时" options={HOURS} value={h} onPick={setH} />
                <div className={styles.colon}>:</div>
                <TimeColumn label="分钟" options={MINUTES} value={m} onPick={setM} />
            </div>
            <div className={styles.footer}>
                <button type="button" className={styles.btn_clear} onClick={onClear}>
                    清除
                </button>
                <button
                    type="button"
                    className={styles.btn_confirm}
                    disabled={!complete}
                    onClick={() => onCommit(`${h}:${m}`)}
                >
                    确定
                </button>
            </div>
        </div>
    );
}

TimePickerPopup.propTypes = {
    value: PropTypes.string,
    onCommit: PropTypes.func.isRequired,
    onClear: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

const TimePickerField = ({ label, value, isOpen, setIsOpen, onSelect }) => {
    const triggerRef = useRef(null);
    const close = () => {
        setIsOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div className={styles_input.input_container}>
            <MdAccessTime className={styles_input.icon} />
            <label>
                <div className={styles_input.label_content}>{label}</div>
            </label>
            <input className={styles_input.input} placeholder="选择时间" value={value || ''} readOnly />
            <button
                ref={triggerRef}
                type="button"
                className={styles_input.trigger_btn}
                onClick={() => setIsOpen(true)}
                aria-label={label}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
            />
            {isOpen && (
                <Floated active={isOpen} setActive={setIsOpen} modal={false} style={{ left: 0, top: '100%' }}>
                    <TimePickerPopup
                        value={value}
                        onCommit={(v) => {
                            onSelect(v);
                            close();
                        }}
                        onClear={() => {
                            onSelect('');
                            close();
                        }}
                        onClose={close}
                    />
                </Floated>
            )}
        </div>
    );
};

TimePickerField.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
};

export default TimePickerField;
